import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Card, Row, Col, Typography, Empty, Spin, Tag, Select, Pagination } from 'antd';
import { useGetOrderLineItemsQuery } from '../../redux/api/order-line-item';
import { Lineitem } from '../../interfaces/Ordel.Interface';
import dayjs from 'dayjs';

const { Meta } = Card;
const { Title } = Typography;

interface AssociateJobModalProps {
    visible: boolean;
    onClose: () => void;
    onAssociate: (job: Lineitem) => void;
    defaultJob?: Lineitem | null;
    defaultProductionMethod?: string;
}

const AssociateJobModal: React.FC<AssociateJobModalProps> = ({
    visible,
    onClose,
    onAssociate,
    defaultJob,
    defaultProductionMethod,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedJob, setSelectedJob] = useState<Lineitem | null>(null);
    const [productionMethod, setProductionMethod] = useState<string | undefined>(undefined);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 20;

    // Reset state when modal opens
    useEffect(() => {
        if (visible) {
            setSearchTerm(defaultJob?.name || '');
            setSelectedJob(defaultJob || null);
            setProductionMethod(defaultProductionMethod || defaultJob?.partsbadger_production_method || undefined);
            setCurrentPage(1);
        }
    }, [visible, defaultJob, defaultProductionMethod]);

    const { data, isLoading } = useGetOrderLineItemsQuery({
        search: searchTerm,
        production_method: productionMethod,
        page_size: pageSize,
        page: currentPage,
        ordering: '-created_at',
    });

    const handleAssociate = () => {
        if (selectedJob) {
            onAssociate(selectedJob);
            onClose();
        }
    };

    return (
        <Modal
            open={visible}
            title={
                <div style={{ textAlign: 'center' }}>
                    <Title level={3} style={{ margin: 0 }}>Associate Job</Title>
                </div>
            }
            onCancel={onClose}
            width={1000}
            centered
            footer={[
                <Button key="cancel" onClick={onClose} size="large">
                    Cancel
                </Button>,
                <Button
                    key="associate"
                    type="primary"
                    onClick={handleAssociate}
                    size="large"
                    disabled={!selectedJob}
                    style={{ backgroundColor: !selectedJob ? undefined : '#444' }}
                >
                    Associate
                </Button>,
            ]}
            styles={{ body: { padding: '2rem', height: '70vh', overflowY: 'auto', backgroundColor: '#f0f2f5', display: 'flex', flexDirection: 'column' } }}
        >
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                <Input.Search
                    placeholder="Search Job/Product"
                    allowClear
                    size="large"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onSearch={(value) => {
                        setSearchTerm(value);
                        setCurrentPage(1); // Reset to page 1 on search
                    }}
                    style={{ width: '50%' }}
                />
                <Select
                    size="large"
                    placeholder="Filter by Method"
                    allowClear
                    style={{ width: '200px' }}
                    value={productionMethod}
                    onChange={(value) => {
                        setProductionMethod(value);
                        setCurrentPage(1); // Reset to page 1 on filter change
                    }}
                    options={[
                        { value: 'Swiss', label: 'Swiss' },
                        { value: 'Fastlane', label: 'Fastlane' },
                    ]}
                />
            </div>

            <div style={{ flex: 1, minHeight: '300px', overflowY: 'auto' }}>
                {isLoading ? (
                    <div style={{ textAlign: 'center', marginTop: '50px' }}>
                        <Spin size="large" />
                    </div>
                ) : !data?.results || data.results.length === 0 ? (
                    <Empty description="No jobs found" />
                ) : (
                    <Row gutter={[16, 16]}>
                        {data.results.map((job: Lineitem) => (
                            <Col span={6} key={job.id}>
                                <Card
                                    hoverable
                                    cover={
                                        <div style={{ padding: '10px', height: '150px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                            {job.thumbnail_file ? (
                                                <img
                                                    alt={job.name}
                                                    src={job.thumbnail_file}
                                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                />
                                            ) : (
                                                <div style={{ color: '#ccc' }}>No Image</div>
                                            )}
                                        </div>
                                    }
                                    style={{
                                        cursor: 'pointer',
                                        border: selectedJob?.id === job.id ? '2px solid #1890ff' : '1px solid #f0f0f0',
                                        backgroundColor: selectedJob?.id === job.id ? '#e6f7ff' : '#fff'
                                    }}
                                    onClick={() => setSelectedJob(job)}
                                >
                                    <Meta
                                        title={<div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={job.name}>{job.name}</div>}
                                        description={
                                            <div style={{ fontSize: '12px' }}>
                                                <div style={{ marginBottom: '4px' }}>{job.order?.name}</div>
                                                <Tag color={job.status === 'Completed' ? 'green' : 'blue'}>
                                                    {job.status}
                                                </Tag>
                                                <div style={{ marginTop: '4px' }}>
                                                    Due: {job.due_date ? dayjs(job.due_date).format('MM-DD-YY') : 'N/A'}
                                                </div>
                                            </div>
                                        }
                                    />
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}
            </div>

            {/* Pagination */}
            {data && data.count > pageSize && (
                <div style={{ marginTop: '1rem', textAlign: 'center', paddingTop: '1rem', borderTop: '1px solid #e8e8e8' }}>
                    <Pagination
                        current={currentPage}
                        total={data.count}
                        pageSize={pageSize}
                        onChange={(page) => setCurrentPage(page)}
                        showSizeChanger={false}
                    />
                </div>
            )}
        </Modal>
    );
};

export default AssociateJobModal;
